import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import {
  AppBaseDropdown,
  AppButton,
  AppInput,
  AppSingleSelect,
} from '@pro-sto/ui';
import type { SelectOption } from '@pro-sto/ui';
import { ShiftAddEmployeePicker } from '@/components/ShiftAddEmployeePicker';
import { ShiftTemplateNameDialog } from '@/components/ShiftTemplateNameDialog';
import {
  ArrowLeftIcon,
  DotsThreeVerticalIcon,
  ExclamationMarkIcon,
  FloppyDiskIcon,
  TrashIcon,
} from '@/components/icons';
import { ROUTES } from '@/constants/routes';
import {
  authStore,
  paymentSchemeTemplatesStore,
  shiftDraftStore,
  shiftTemplatesStore,
} from '@/stores';
import {
  emptyTemplateDraftRow,
  hasShiftTemplateErrors,
  templateClock,
  toCreateShiftTemplatePayload,
  toUpdateShiftTemplatePayload,
  validateShiftTemplate,
  type ShiftTemplateDraftRow,
  type ShiftTemplateErrors,
} from '@/stores/shiftTemplateModel';
import { completeClock } from '@/stores/shiftDraftModel';
import './ShiftTemplateEditPage.css';

const NO_TEMPLATE_OPTION: SelectOption = { label: 'Без шаблона', value: null };
const SELECT_DROPDOWN = { dropdownWidth: 'equal-toggle' as const };
const UNAVAILABLE_SCHEME_LABEL = 'Схема недоступна';

const employeeName = (lastName: string, firstName: string) =>
  `${lastName} ${firstName}`.trim();

const initials = (lastName: string, firstName: string) =>
  `${lastName?.[0] ?? ''}${firstName?.[0] ?? ''}`.toUpperCase();

const uniqueMessages = (...messages: Array<string | undefined>): string[] =>
  [...new Set(messages.filter((message): message is string => Boolean(message)))];

interface LocalRow extends ShiftTemplateDraftRow {
  employeeName: string | null;
}

interface DraftSnapshot {
  name: string;
  responsibleEmployeeUuid: string | null;
  rows: Array<{
    employeeUuid: string | null;
    plannedStart: string;
    plannedEnd: string;
    paymentSchemeTemplateUuid: string | null;
  }>;
}

type LeaveTarget =
  | { kind: 'settings' }
  | { kind: 'new' }
  | { kind: 'edit'; uuid: string };

const EMPTY_SNAPSHOT: DraftSnapshot = {
  name: '',
  responsibleEmployeeUuid: null,
  rows: [],
};

const rowSnapshot = (row: LocalRow) => ({
  employeeUuid: row.employeeUuid,
  plannedStart: row.plannedStart,
  plannedEnd: row.plannedEnd,
  paymentSchemeTemplateUuid: row.paymentSchemeTemplateUuid,
});

const snapshotFromRows = (
  name: string,
  responsibleEmployeeUuid: string | null,
  rows: LocalRow[],
): DraftSnapshot => ({
  name,
  responsibleEmployeeUuid,
  rows: rows
    .map(rowSnapshot)
    .sort((left, right) => (left.employeeUuid ?? '').localeCompare(right.employeeUuid ?? '')),
});

const snapshotsEqual = (left: DraftSnapshot, right: DraftSnapshot) =>
  JSON.stringify(left) === JSON.stringify(right);

const RowRemoveIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path d="M4 10H16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const emptyErrors = (): ShiftTemplateErrors => ({ rows: {} });

const resetDraftState = (
  setLoadedName: (value: string) => void,
  setResponsibleEmployeeUuid: (value: string | null) => void,
  setRows: (value: LocalRow[]) => void,
  setFieldErrors: (value: ShiftTemplateErrors) => void,
) => {
  setLoadedName('');
  setResponsibleEmployeeUuid(null);
  setRows([]);
  setFieldErrors(emptyErrors());
};

const ShiftTemplateDetailSkeleton: React.FC = () => (
  <div className="shift-template-edit-page__state" data-testid="shift-template-loading">
    <span className="shift-template-edit-page__skeleton shift-template-edit-page__skeleton_field" />
    <span className="shift-template-edit-page__skeleton shift-template-edit-page__skeleton_block" />
    <span className="shift-template-edit-page__skeleton shift-template-edit-page__skeleton_row" />
    <span className="shift-template-edit-page__skeleton shift-template-edit-page__skeleton_row" />
  </div>
);

interface ShiftTemplateLoadErrorProps {
  message: string;
  onRetry: () => void;
  onCreateNew: () => void;
}

const ShiftTemplateLoadError: React.FC<ShiftTemplateLoadErrorProps> = ({
  message,
  onRetry,
  onCreateNew,
}) => (
  <div
    className="shift-template-edit-page__state shift-template-edit-page__state_notice"
    data-testid="shift-template-load-error"
  >
    <ExclamationMarkIcon size={24} />
    <p className="shift-template-edit-page__submit-error">{message}</p>
    <div className="shift-template-edit-page__state-actions">
      <AppButton variant="secondary" onClick={onRetry} data-testid="shift-template-load-retry">
        Повторить
      </AppButton>
      <AppButton variant="secondary" onClick={onCreateNew} data-testid="shift-template-load-new">
        Без шаблона
      </AppButton>
    </div>
  </div>
);

const ShiftTemplateEditPage: React.FC = observer(() => {
  const navigate = useNavigate();
  const { uuid } = useParams<{ uuid: string }>();
  const isCreate = !uuid;
  const serviceCenterUuid = authStore.user?.service_center_uuid;

  const [loadedName, setLoadedName] = useState('');
  const [responsibleEmployeeUuid, setResponsibleEmployeeUuid] = useState<string | null>(null);
  const [rows, setRows] = useState<LocalRow[]>([]);
  const [fieldErrors, setFieldErrors] = useState<ShiftTemplateErrors>(emptyErrors());
  const [nameDialogOpen, setNameDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [leaveConfirmOpen, setLeaveConfirmOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const baselineRef = useRef<DraftSnapshot>(EMPTY_SNAPSHOT);
  const pendingLeaveRef = useRef<LeaveTarget | null>(null);

  useEffect(() => {
    if (!serviceCenterUuid) return;
    shiftTemplatesStore.setServiceCenterUuid(serviceCenterUuid);
    paymentSchemeTemplatesStore.setServiceCenterUuid(serviceCenterUuid);
    void shiftDraftStore.loadEmployees(serviceCenterUuid);
  }, [serviceCenterUuid]);

  useLayoutEffect(() => {
    resetDraftState(setLoadedName, setResponsibleEmployeeUuid, setRows, setFieldErrors);
    setNameDialogOpen(false);
    setDeleteDialogOpen(false);
    setMenuOpen(false);

    if (isCreate) {
      baselineRef.current = EMPTY_SNAPSHOT;
      shiftTemplatesStore.clearActiveTemplate();
      return;
    }
    if (uuid) {
      void shiftTemplatesStore.loadTemplateDetail(uuid);
    }
  }, [uuid, isCreate]);

  const activeTemplate = shiftTemplatesStore.activeTemplate;
  const detailMismatch = !isCreate && activeTemplate?.uuid !== uuid;
  const detailLoadFailed =
    !isCreate &&
    !shiftTemplatesStore.isLoadingTemplate &&
    Boolean(shiftTemplatesStore.submitError) &&
    activeTemplate?.uuid !== uuid;
  const waitingForDetail =
    !isCreate &&
    !detailLoadFailed &&
    (shiftTemplatesStore.isLoadingTemplate ||
      (detailMismatch && !shiftTemplatesStore.submitError));
  const actionsLocked =
    !isCreate &&
    !detailLoadFailed &&
    (shiftTemplatesStore.isLoadingTemplate || detailMismatch);

  useEffect(() => {
    if (isCreate || !activeTemplate || activeTemplate.uuid !== uuid) return;
    const nextRows = activeTemplate.rows.map((row) => ({
      key: row.uuid,
      employeeUuid: row.employee_uuid,
      employeeName: row.name,
      position: row.position,
      plannedStart: templateClock(row.planned_start),
      plannedEnd: templateClock(row.planned_end),
      paymentSchemeTemplateUuid: row.payment_scheme_template_uuid,
    }));
    setLoadedName(activeTemplate.name);
    setResponsibleEmployeeUuid(activeTemplate.responsible_employee_uuid);
    setRows(nextRows);
    baselineRef.current = snapshotFromRows(
      activeTemplate.name,
      activeTemplate.responsible_employee_uuid,
      nextRows,
    );
    setFieldErrors(emptyErrors());
  }, [activeTemplate, isCreate, uuid]);

  const currentSnapshot = () =>
    snapshotFromRows(loadedName, responsibleEmployeeUuid, rows);

  const isDirty = () => !snapshotsEqual(currentSnapshot(), baselineRef.current);

  const executeLeave = (target: LeaveTarget) => {
    if (target.kind === 'settings') {
      navigate(ROUTES.SETTINGS);
      return;
    }
    if (target.kind === 'new') {
      navigate(ROUTES.SHIFT_TEMPLATE_NEW);
      return;
    }
    navigate(generatePath(ROUTES.SHIFT_TEMPLATE_EDIT, { uuid: target.uuid }));
  };

  const requestLeave = (target: LeaveTarget) => {
    if (!isDirty()) {
      executeLeave(target);
      return;
    }
    pendingLeaveRef.current = target;
    setLeaveConfirmOpen(true);
  };

  const confirmLeave = () => {
    const target = pendingLeaveRef.current;
    pendingLeaveRef.current = null;
    setLeaveConfirmOpen(false);
    if (target) executeLeave(target);
  };

  const handleCancel = () => requestLeave({ kind: 'settings' });

  const templateOptions: SelectOption[] = [
    NO_TEMPLATE_OPTION,
    ...shiftTemplatesStore.templates.map((template) => ({
      label: template.name,
      value: template.uuid,
    })),
  ];
  const selectedTemplate = uuid
    ? templateOptions.find((option) => option.value === uuid) ?? {
        label: loadedName || 'Шаблон',
        value: uuid,
      }
    : NO_TEMPLATE_OPTION;

  const handleTemplatePick = (option: SelectOption | null) => {
    const value = (option?.value as string | null) ?? null;
    if (!value) {
      if (!isCreate) requestLeave({ kind: 'new' });
      return;
    }
    if (value !== uuid) {
      requestLeave({ kind: 'edit', uuid: value });
    }
  };

  const rosterIds = new Set(
    rows.map((row) => row.employeeUuid).filter((id): id is string => Boolean(id)),
  );

  const responsibleOptions: SelectOption[] = rows.flatMap((row) => {
    if (!row.employeeUuid) return [];
    const employee = shiftDraftStore.employees.find((item) => item.uuid === row.employeeUuid);
    const label = employee
      ? employeeName(employee.last_name, employee.first_name)
      : row.employeeName;
    if (!label) return [];
    return [{ value: row.employeeUuid, label }];
  });
  const responsibleValue =
    responsibleOptions.find((option) => option.value === responsibleEmployeeUuid) ?? null;

  const validateDraft = (): boolean => {
    const completedRows = rows.map((row) => ({
      ...row,
      plannedStart: completeClock(row.plannedStart),
      plannedEnd: completeClock(row.plannedEnd),
    }));
    setRows(completedRows);
    const errors = validateShiftTemplate({
      responsibleEmployeeUuid,
      rows: completedRows,
    });
    setFieldErrors(errors);
    return !hasShiftTemplateErrors(errors);
  };

  const handleSaveClick = () => {
    if (actionsLocked) return;
    if (!validateDraft()) return;
    if (isCreate) {
      setNameDialogOpen(true);
      return;
    }
    void saveExisting();
  };

  const saveExisting = async () => {
    if (!uuid || !serviceCenterUuid || !responsibleEmployeeUuid) return;
    if (actionsLocked) return;
    const payload = toUpdateShiftTemplatePayload({
      name: loadedName,
      responsibleEmployeeUuid,
      rows,
    });
    await shiftTemplatesStore.updateTemplate(uuid, payload);
  };

  const handleNameSave = async (name: string) => {
    if (!serviceCenterUuid || !responsibleEmployeeUuid) return;
    const payload = toCreateShiftTemplatePayload({
      name,
      serviceCenterUuid,
      responsibleEmployeeUuid,
      rows,
    });
    const created = await shiftTemplatesStore.createTemplate(payload);
    if (!created) return;
    setNameDialogOpen(false);
    navigate(generatePath(ROUTES.SHIFT_TEMPLATE_EDIT, { uuid: created.uuid }));
  };

  const handleDelete = async () => {
    if (!uuid || actionsLocked) return;
    const ok = await shiftTemplatesStore.deleteTemplate(uuid);
    if (!ok) return;
    setDeleteDialogOpen(false);
    navigate(ROUTES.SHIFT_TEMPLATE_NEW);
  };

  const addEmployee = (employeeUuid: string) => {
    const employee = shiftDraftStore.employees.find((item) => item.uuid === employeeUuid);
    setRows((current) => {
      if (current.some((row) => row.employeeUuid === employeeUuid)) return current;
      return [
        ...current,
        {
          ...emptyTemplateDraftRow(),
          employeeUuid,
          employeeName: employee
            ? employeeName(employee.last_name, employee.first_name)
            : null,
          position: employee?.position.name ?? null,
        },
      ];
    });
    setFieldErrors(emptyErrors());
  };

  const removeRow = (key: string) => {
    setRows((current) => current.filter((row) => row.key !== key));
    setFieldErrors((current) => {
      if (!current.rows[key]) return current;
      const nextRows = { ...current.rows };
      delete nextRows[key];
      return { ...current, rows: nextRows };
    });
  };

  const setRowTime = (key: string, edge: 'start' | 'end', value: string) => {
    setRows((current) =>
      current.map((row) => {
        if (row.key !== key) return row;
        return edge === 'start' ? { ...row, plannedStart: value } : { ...row, plannedEnd: value };
      }),
    );
  };

  const completeRowTime = (key: string, edge: 'start' | 'end', raw?: string) => {
    setRows((current) =>
      current.map((row) => {
        if (row.key !== key) return row;
        const stored = edge === 'start' ? row.plannedStart : row.plannedEnd;
        const completed = completeClock(raw ?? stored);
        if (completed === stored) return row;
        return edge === 'start'
          ? { ...row, plannedStart: completed }
          : { ...row, plannedEnd: completed };
      }),
    );
  };

  const setRowScheme = (key: string, templateUuid: string | null) => {
    setRows((current) =>
      current.map((row) =>
        row.key === key ? { ...row, paymentSchemeTemplateUuid: templateUuid } : row,
      ),
    );
  };

  const pageError =
    fieldErrors.form ||
    fieldErrors.responsible ||
    shiftTemplatesStore.submitError ||
    null;

  const formDisabled =
    actionsLocked || shiftTemplatesStore.isSaving || shiftTemplatesStore.isDeleting;

  return (
    <div className="shift-template-edit-page" data-testid="shift-template-page">
      <div className="shift-template-edit-page__container">
        <div className="shift-template-edit-page__header">
          <div className="shift-template-edit-page__header-left">
            <button
              type="button"
              className="shift-template-edit-page__back-button"
              onClick={handleCancel}
              aria-label="Назад"
              data-testid="shift-template-back"
            >
              <ArrowLeftIcon size={20} />
            </button>
            <h1 className="shift-template-edit-page__title">Редактировать шаблон смены</h1>
          </div>
          <div className="shift-template-edit-page__header-actions">
            <AppButton
              variant="secondary"
              onClick={handleCancel}
              disabled={shiftTemplatesStore.isSaving || shiftTemplatesStore.isDeleting}
              data-testid="shift-template-cancel"
            >
              Отменить
            </AppButton>
            <AppButton
              variant="primary"
              iconRight={<FloppyDiskIcon size={16} />}
              onClick={handleSaveClick}
              loading={shiftTemplatesStore.isSaving}
              disabled={actionsLocked || shiftTemplatesStore.isDeleting}
              data-testid="shift-template-save"
            >
              Сохранить
            </AppButton>
            {isCreate ? (
              <button
                type="button"
                className="shift-template-edit-page__menu"
                disabled
                aria-label="Меню шаблона"
                data-testid="shift-template-menu"
              >
                <DotsThreeVerticalIcon size={20} />
              </button>
            ) : (
              <AppBaseDropdown
                opened={menuOpen}
                onClose={() => setMenuOpen(false)}
                dropdownWidth="220px"
                xDirection="left"
                yDirection="bottom"
                toggle={
                  <button
                    type="button"
                    className="shift-template-edit-page__menu shift-template-edit-page__menu_active"
                    aria-label="Меню шаблона"
                    disabled={actionsLocked || shiftTemplatesStore.isDeleting}
                    onClick={() => setMenuOpen((open) => !open)}
                    data-testid="shift-template-menu"
                  >
                    <DotsThreeVerticalIcon size={20} />
                  </button>
                }
                dropdown={
                  <div className="shift-template-edit-page__menu-panel" role="menu">
                    <button
                      type="button"
                      role="menuitem"
                      className="shift-template-edit-page__menu-item shift-template-edit-page__menu-item_danger"
                      disabled={actionsLocked}
                      onClick={() => {
                        setMenuOpen(false);
                        setDeleteDialogOpen(true);
                      }}
                      data-testid="shift-template-delete"
                    >
                      <TrashIcon size={16} />
                      <span>Удалить</span>
                    </button>
                  </div>
                }
              />
            )}
          </div>
        </div>

        {pageError ? (
          <p
            className="shift-template-edit-page__submit-error"
            title={pageError}
            data-testid="shift-template-error"
          >
            {pageError}
          </p>
        ) : null}

        <section className="shift-template-edit-page__section">
          <h2 className="shift-template-edit-page__section-title">Выбрать шаблон</h2>
          <p
            className="shift-template-edit-page__hint"
            data-testid="shift-template-caption"
          >
            Создайте новый или редактируйте существующие.
          </p>
          <div className="shift-template-edit-page__picker">
            <AppSingleSelect
              placeholder="Без шаблона"
              options={templateOptions}
              value={selectedTemplate}
              onChange={handleTemplatePick}
              disabled={
                shiftTemplatesStore.templatesLoading ||
                shiftTemplatesStore.isSaving ||
                shiftTemplatesStore.isDeleting
              }
              baseDropdownProps={SELECT_DROPDOWN}
              data-testid="shift-template-picker"
            />
          </div>
        </section>

        {waitingForDetail ? (
          <ShiftTemplateDetailSkeleton />
        ) : detailLoadFailed && uuid ? (
          <ShiftTemplateLoadError
            message={shiftTemplatesStore.submitError ?? 'Не удалось загрузить шаблон смены'}
            onRetry={() => {
              void shiftTemplatesStore.loadTemplateDetail(uuid);
            }}
            onCreateNew={() => requestLeave({ kind: 'new' })}
          />
        ) : (
          <>
            <section className="shift-template-edit-page__section">
              <div className="shift-template-edit-page__responsible">
                <AppSingleSelect
                  label="Ответственный"
                  placeholder="Введите имя"
                  options={responsibleOptions}
                  value={responsibleValue}
                  onChange={(option) =>
                    setResponsibleEmployeeUuid((option?.value as string | null) ?? null)
                  }
                  error={fieldErrors.responsible}
                  clearable
                  disabled={formDisabled}
                  baseDropdownProps={SELECT_DROPDOWN}
                  data-testid="shift-template-responsible"
                />
              </div>
            </section>

            <section className="shift-template-edit-page__section">
              <h2 className="shift-template-edit-page__section-title">Состав смены</h2>
              <ShiftAddEmployeePicker
                takenIds={rosterIds}
                onPick={addEmployee}
                disabled={formDisabled}
                data-testid="shift-template-add-employee"
              />
              <div className="shift-template-edit-page__table">
                <div className="shift-template-edit-page__row shift-template-edit-page__row_head">
                  <span>Сотрудник</span>
                  <span>Роль</span>
                  <span>Время</span>
                  <span>Схема оплаты</span>
                  <span />
                </div>
                {rows.map((row) => (
                  <TemplateMemberRow
                    key={row.key}
                    row={row}
                    errors={fieldErrors.rows[row.key]}
                    disabled={formDisabled}
                    onRemove={() => removeRow(row.key)}
                    onTimeChange={setRowTime}
                    onTimeComplete={completeRowTime}
                    onSchemeChange={setRowScheme}
                  />
                ))}
              </div>
            </section>
          </>
        )}
      </div>

      <ShiftTemplateNameDialog
        isOpen={nameDialogOpen}
        isSaving={shiftTemplatesStore.isSaving}
        onSave={handleNameSave}
        onCancel={() => setNameDialogOpen(false)}
      />

      {leaveConfirmOpen ? (
        <div
          className="shift-template-edit-page__dialog-overlay"
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              pendingLeaveRef.current = null;
              setLeaveConfirmOpen(false);
            }
          }}
        >
          <div
            className="shift-template-edit-page__dialog"
            role="dialog"
            aria-modal="true"
            data-testid="shift-template-leave-dialog"
          >
            <h2 className="shift-template-edit-page__dialog-title">Выйти без сохранения?</h2>
            <p className="shift-template-edit-page__dialog-message">
              Несохранённые изменения будут потеряны.
            </p>
            <div className="shift-template-edit-page__dialog-actions">
              <AppButton
                variant="secondary"
                onClick={() => {
                  pendingLeaveRef.current = null;
                  setLeaveConfirmOpen(false);
                }}
                data-testid="shift-template-leave-cancel"
              >
                Остаться
              </AppButton>
              <AppButton
                variant="primary"
                onClick={confirmLeave}
                data-testid="shift-template-leave-confirm"
              >
                Выйти
              </AppButton>
            </div>
          </div>
        </div>
      ) : null}

      {deleteDialogOpen ? (
        <div
          className="shift-template-edit-page__dialog-overlay"
          onClick={(event) => {
            if (event.target === event.currentTarget && !shiftTemplatesStore.isDeleting) {
              setDeleteDialogOpen(false);
            }
          }}
        >
          <div
            className="shift-template-edit-page__dialog"
            role="dialog"
            aria-modal="true"
            data-testid="shift-template-delete-dialog"
          >
            <h2 className="shift-template-edit-page__dialog-title">Удалить шаблон?</h2>
            <p className="shift-template-edit-page__dialog-message">
              {`Вы действительно хотите удалить шаблон «${loadedName}»?`}
            </p>
            <div className="shift-template-edit-page__dialog-actions">
              <AppButton
                variant="secondary"
                onClick={() => setDeleteDialogOpen(false)}
                disabled={shiftTemplatesStore.isDeleting}
                data-testid="shift-template-delete-cancel"
              >
                Отменить
              </AppButton>
              <AppButton
                variant="primary"
                onClick={handleDelete}
                loading={shiftTemplatesStore.isDeleting}
                disabled={actionsLocked}
                data-testid="shift-template-delete-confirm"
              >
                Удалить
              </AppButton>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
});

interface TemplateMemberRowProps {
  row: LocalRow;
  errors?: ShiftTemplateErrors['rows'][string];
  disabled?: boolean;
  onRemove: () => void;
  onTimeChange: (key: string, edge: 'start' | 'end', value: string) => void;
  onTimeComplete: (key: string, edge: 'start' | 'end', raw?: string) => void;
  onSchemeChange: (key: string, templateUuid: string | null) => void;
}

const TemplateMemberRow: React.FC<TemplateMemberRowProps> = observer(({
  row,
  errors,
  disabled = false,
  onRemove,
  onTimeChange,
  onTimeComplete,
  onSchemeChange,
}) => {
  const employee = shiftDraftStore.employees.find((item) => item.uuid === row.employeeUuid);
  const name = employee
    ? employeeName(employee.last_name, employee.first_name)
    : row.employeeName ?? '';
  const position = employee?.position.name ?? row.position ?? '—';
  const avatar = employee
    ? initials(employee.last_name, employee.first_name)
    : initials(name.split(/\s+/)[0] ?? '', name.split(/\s+/)[1] ?? '');

  const schemeOptions: SelectOption[] = [...paymentSchemeTemplatesStore.activeOptions];
  if (
    row.paymentSchemeTemplateUuid &&
    !schemeOptions.some((option) => option.value === row.paymentSchemeTemplateUuid)
  ) {
    schemeOptions.push({
      value: row.paymentSchemeTemplateUuid,
      label: UNAVAILABLE_SCHEME_LABEL,
    });
  }
  const schemeValue =
    schemeOptions.find((option) => option.value === row.paymentSchemeTemplateUuid) ?? null;

  return (
    <div className="shift-template-edit-page__row" data-testid={`shift-template-row-${row.key}`}>
      <div className="shift-template-edit-page__employee">
        {name ? (
          <span className="shift-template-edit-page__avatar" aria-hidden="true">
            {avatar}
          </span>
        ) : null}
        <span
          className={`shift-template-edit-page__employee-name${name ? '' : ' shift-template-edit-page__employee-name_empty'}`}
          title={name || undefined}
        >
          {name || '—'}
        </span>
        {errors?.employee ? (
          <span className="shift-template-edit-page__row-error">{errors.employee}</span>
        ) : null}
      </div>
      <span
        className="shift-template-edit-page__position"
        title={position}
        data-testid={`shift-template-role-${row.key}`}
      >
        {position}
      </span>
      <div className="shift-template-edit-page__time">
        <div className="shift-template-edit-page__time-fields">
          <div className="shift-template-edit-page__time-input">
            <AppInput
              size="M"
              value={row.plannedStart}
              placeholder="00:00"
              mask="00:00"
              lazy
              error={Boolean(errors?.plannedStart)}
              disabled={disabled}
              onChange={(value) => onTimeChange(row.key, 'start', value)}
              onBlur={(event) => {
                const raw = event.currentTarget.value;
                window.setTimeout(() => onTimeComplete(row.key, 'start', raw), 0);
              }}
              data-testid={`shift-template-start-${row.key}`}
            />
          </div>
          <span className="shift-template-edit-page__time-dash" aria-hidden="true">
            –
          </span>
          <div className="shift-template-edit-page__time-input">
            <AppInput
              size="M"
              value={row.plannedEnd}
              placeholder="00:00"
              mask="00:00"
              lazy
              error={Boolean(errors?.plannedEnd)}
              disabled={disabled}
              onChange={(value) => onTimeChange(row.key, 'end', value)}
              onBlur={(event) => {
                const raw = event.currentTarget.value;
                window.setTimeout(() => onTimeComplete(row.key, 'end', raw), 0);
              }}
              data-testid={`shift-template-end-${row.key}`}
            />
          </div>
        </div>
        {uniqueMessages(errors?.plannedStart, errors?.plannedEnd).map((message) => (
          <span key={message} className="shift-template-edit-page__time-error" title={message}>
            {message}
          </span>
        ))}
      </div>
      <div className="shift-template-edit-page__scheme">
        <AppSingleSelect
          placeholder="Схема оплаты"
          options={schemeOptions}
          value={schemeValue}
          onChange={(option) =>
            onSchemeChange(row.key, (option?.value as string | null) ?? null)
          }
          clearable
          disabled={disabled}
          baseDropdownProps={SELECT_DROPDOWN}
          data-testid={`shift-template-scheme-${row.key}`}
        />
      </div>
      <div className="shift-template-edit-page__row-actions">
        <button
          type="button"
          className="shift-template-edit-page__menu-row"
          disabled
          aria-label="Меню строки"
          data-testid={`shift-template-row-menu-${row.key}`}
        >
          <DotsThreeVerticalIcon size={16} />
        </button>
        <button
          type="button"
          className="shift-template-edit-page__row-remove"
          onClick={onRemove}
          disabled={disabled}
          aria-label="Убрать строку"
          data-testid={`shift-template-row-remove-${row.key}`}
        >
          <RowRemoveIcon />
        </button>
      </div>
    </div>
  );
});

TemplateMemberRow.displayName = 'ShiftTemplateMemberRow';
ShiftTemplateEditPage.displayName = 'ShiftTemplateEditPage';

export default ShiftTemplateEditPage;
